import { WidgetInputComplexNumericField, WidgetInputSliderField, WidgetLabel } from '../../../../rapid-ui'
import type { Background, BorderRadiusCorner, BorderRadiusCorners, Spacing } from '../../data/v1'
import { BackgroundInspector } from './background'
import { SpacingInspector } from './spacing'

const cornerItems: { id: BorderRadiusCorner; label: string; innerLabel: string; arcClassName: string }[] = [
	{ id: 'topLeft', label: 'Top left', innerLabel: 'TL', arcClassName: 'border-t border-l rounded-tl-[10px]' },
	{ id: 'topRight', label: 'Top right', innerLabel: 'TR', arcClassName: 'border-t border-r rounded-tr-[10px]' },
	{ id: 'bottomLeft', label: 'Bottom left', innerLabel: 'BL', arcClassName: 'border-b border-l rounded-bl-[10px]' },
	{
		id: 'bottomRight',
		label: 'Bottom right',
		innerLabel: 'BR',
		arcClassName: 'border-b border-r rounded-br-[10px]',
	},
]

type CornerCheckboxesProps = {
	value: BorderRadiusCorners
	onChange: (corners: BorderRadiusCorners) => void
}

function CornerCheckboxes({ value, onChange }: CornerCheckboxesProps) {
	return (
		<div className="grid grid-cols-2 gap-1">
			{cornerItems.map(({ id, label, innerLabel, arcClassName }) => (
				<label key={id} className="flex cursor-pointer items-center gap-1.5 text-xs text-grey-500">
					<input
						type="checkbox"
						checked={value[id]}
						onChange={(event) =>
							onChange({ ...value, [id]: event.target.checked })}
						aria-label={label}
						className="size-3.5 cursor-pointer accent-blue-500"
					/>
					<span aria-hidden="true" className={`size-2.5 border-grey-400 ${arcClassName}`} />
					{innerLabel}
				</label>
			))}
		</div>
	)
}

export type BoxInspectorProps = {
	layerId: string
	opacity?: number
	onOpacityChange?: (opacity: number) => void
	onTransientOpacityChange?: (opacity: number) => void
	background?: Background
	onBackgroundChange?: (updates: Partial<Background>, options?: { transient?: boolean }) => void
	borderRadius?: number
	onBorderRadiusChange?: (borderRadius: number) => void
	borderRadiusCorners?: BorderRadiusCorners
	onBorderRadiusCornersChange?: (corners: BorderRadiusCorners) => void
	spacing: Spacing
	onSpacingChange: (spacing: Spacing) => void
	onTransientSpacingChange?: (spacing: Spacing) => void
}

export function BoxInspector(
	{
		layerId,
		opacity,
		onOpacityChange,
		onTransientOpacityChange,
		background,
		onBackgroundChange,
		borderRadius,
		onBorderRadiusChange,
		borderRadiusCorners,
		onBorderRadiusCornersChange,
		spacing,
		onSpacingChange,
		onTransientSpacingChange,
	}: BoxInspectorProps,
) {
	return (
		<div className="flex flex-col gap-2">
			{opacity !== undefined && onOpacityChange && (
				<WidgetLabel label="Opacity">
					<WidgetInputSliderField
						label="Opacity"
						value={opacity}
						onChange={onOpacityChange}
						onTransientChange={onTransientOpacityChange}
						min={0}
						max={100}
						suffix="%"
					/>
				</WidgetLabel>
			)}

			{background !== undefined && onBackgroundChange && (
				<BackgroundInspector
					layerId={layerId}
					value={background}
					onChange={onBackgroundChange}
				/>
			)}

			{borderRadius !== undefined && onBorderRadiusChange && (
				<WidgetLabel label="Border radius">
					<WidgetInputComplexNumericField
						value={borderRadius}
						onChange={onBorderRadiusChange}
						min={0}
						suffix="px"
						defaultValue={0}
					/>
				</WidgetLabel>
			)}

			{borderRadius !== undefined && borderRadius > 0 && borderRadiusCorners !== undefined
				&& onBorderRadiusCornersChange && (
				<WidgetLabel label="Rounded corners">
					<CornerCheckboxes value={borderRadiusCorners} onChange={onBorderRadiusCornersChange} />
				</WidgetLabel>
			)}

			<SpacingInspector
				value={spacing}
				onChange={onSpacingChange}
				onTransientChange={onTransientSpacingChange}
			/>
		</div>
	)
}
